/* qp-nav.js — global chrome behavior (Wave 1 drawer navigation).
   DISPLAY-ONLY: nothing here touches line-item properties, add-to-cart
   payloads, or pricing. Four responsibilities:

   1. <qp-header> sticky controller — 64->56 condense after 80px, hide on
      scroll-down past 200px, reveal on scroll-up (240ms transform-only,
      instant under prefers-reduced-motion). Mobile-PDP guard: while the
      sticky ATC bar is visible (documentElement.stick-atc-show, set by
      Minimog's sticky-atc.js IntersectionObserver) the header never
      re-pins on scroll-up — two pinned bars never coexist and the money
      CTA is never occluded. Also republishes the Minimog layout vars
      (--m-header-height / MinimogSettings) other sections consume.

   2. Door drawer controller (qp-shell.css contract) — click/tap on a
      door ([data-qp-drawer-open]) OPENS its drawer; hover NEVER opens
      (the old hover-intent was retired with the panel system, and the
      F6§14 BIKES hover-peek was retired same-day by PM live test
      2026-07-12 — REV D click-only is signed canon). Esc /
      outside click / ✕ close; focus is trapped inside; focus returns to
      the door on close; ONE drawer open at a time; body scroll-lock via
      the qp-scroll-lock class. Open sequence mirrors the shell contract:
      unhide, then double-rAF .is-open so the transform transition runs.
      Side (left/right) is pure CSS via the drawer's [data-side].

   3. Mobile LEFT sheet + market selector (listbox semantics, country and
      currency switch together via the native localization form). Sheet
      disclosure is EXCLUSIVE: expanding a door row collapses the others,
      and the expanded row carries .is-expanded (the kinetic-lime hook —
      max one per view).

   4. Cart-bubble count override — Minimog's updateCartCount writes raw
      cart.item_count into .m-cart-count-bubble; the signed count
      semantics are BUILDS + ITEMS (classifier). Same prototype-patch
      pattern as qp-cart-build.js: wait for the custom elements, replace
      updateCartCount, and re-render the count from the server
      (GET /?sections=qp-nav-count -> snippets/qp-cart-bubble-count) so
      the classifier stays the single source of truth. A
      MutationObserver backstop catches direct writers (e.g. the
      theme-global.js no-drawer fallback path). */
(function () {
  'use strict';

  console.info("Quick Pro USA — original theme code © quickprousa.com");

  if (window.__qpNavLoaded) return;
  window.__qpNavLoaded = true;

  var reducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  /* ── 0 · portal drawer shells + mobile sheet OUT of the sticky header ───
     DRAWER-STACKING-TRAP: the shell/sheet roots render inside the
     header's .shopify-section wrapper, which this file itself makes
     position:sticky (qp-header-sticky-wrap, z-index:90) — a stacking
     context. Their real z-index values (1000000/1000001, qp-nav.css /
     qp-shell.css) are trapped INSIDE that context, so at the page root
     the whole nav only ever competes at z:90. Anything root-level with
     z>90 then paints OVER the open panels — confirmed twice: the
     store-locator's Leaflet map (patched separately with
     isolation:isolate — kept, unrelated to this fix) and the Powerful
     Form Builder app embed bleeding through the open mobile sheet.
     Panels must NEVER live inside the sticky wrapper. Moving them to
     document.body (they're position:fixed, so geometry is unaffected)
     makes the z-index actually compete at root level — above any app
     embed, and still above the chat launcher (999997-999999).

     This is safe because every drawer/sheet lookup elsewhere in this
     file is already ID- or containment-based (getElementById(),
     .contains(), .closest() scoped to the panel/root itself) rather
     than reaching UP through a header-scoped ancestor — open, close,
     outside-click, and focus-trap all keep working untouched after
     the move.

     Idempotent for the theme editor: on shopify:section:load, the
     section that just re-rendered may contain FRESH (unportaled) panel
     markup while the OLD portaled nodes still sit on body from the
     previous render — removing the stale ones first is what stops a
     re-save from ever duplicating them. */

  function portalNavPanels() {
    document.querySelectorAll('[data-qp-shell]').forEach(function (root) {
      if (root.parentNode !== document.body) document.body.appendChild(root);
    });
    var sheet = document.querySelector('[data-qp-sheet-root]');
    if (sheet && sheet.parentNode !== document.body) {
      document.body.appendChild(sheet);
    }
  }

  function deportalStaleNavPanels() {
    /* querySelectorAll + filter-by-parent, NOT querySelector — document
       order can put a FRESH (just-reloaded, still inside the wrapper)
       node before the STALE one already on body, so grabbing "the
       first match anywhere" risks removing the wrong one and leaking
       the real stale node as a duplicate. */
    Array.prototype.filter
      .call(document.querySelectorAll('[data-qp-shell]'), function (el) {
        return el.parentNode === document.body;
      })
      .forEach(function (el) {
        el.parentNode.removeChild(el);
      });
    Array.prototype.filter
      .call(document.querySelectorAll('[data-qp-sheet-root]'), function (el) {
        return el.parentNode === document.body;
      })
      .forEach(function (el) {
        el.parentNode.removeChild(el);
      });
  }

  portalNavPanels();

  /* shopify:section:load only ever fires inside the theme editor
     (design-mode class set synchronously in <head> by layout/theme.
     liquid, always before this deferred script runs) */
  if (document.documentElement.classList.contains('shopify-design-mode')) {
    document.addEventListener('shopify:section:load', function (e) {
      var target = e && e.target;
      if (!target || !target.querySelector) return;
      var hasFreshPanels =
        target.querySelector('qp-header') ||
        target.querySelector('[data-qp-shell]') ||
        target.querySelector('[data-qp-sheet-root]');
      if (!hasFreshPanels) return;
      deportalStaleNavPanels();
      portalNavPanels();
    });
  }

  /* ── 1 · <qp-header> sticky controller ─────────────────────────────── */

  var CONDENSE_AT = 80;
  var HIDE_AFTER = 200;

  function atcPinned() {
    return document.documentElement.classList.contains('stick-atc-show');
  }

  /* Minimog layout contract (regression fix 2026-07-11): the old header
     published these CSS vars + MinimogSettings values and other sections
     consume them — slideshow.css fullscreen height calc,
     featured-collection-banner.css sticky offset, sticky-atc.js
     rootMargin. Without them they silently fall back to 0px / 66px. */
  function syncMinimogLayoutVars(headerEl) {
    var root = document.documentElement;
    window.MinimogSettings = window.MinimogSettings || {};
    var h = headerEl.offsetHeight;
    root.style.setProperty('--m-header-height', h + 'px');
    window.MinimogSettings.headerHeight = h;
    var ab = document.querySelector('m-announcement-bar, .m-announcement-bar, .qp-announcement');
    if (ab) {
      root.style.setProperty('--m-announcement-height', ab.offsetHeight + 'px');
      window.MinimogSettings.topbarHeight = ab.offsetHeight;
    }
    var tb = document.querySelector('.m-topbar');
    if (tb) {
      root.style.setProperty('--m-topbar-height', tb.offsetHeight + 'px');
      window.MinimogSettings.topbarHeight = tb.offsetHeight;
    }
  }

  if (!customElements.get('qp-header')) {
    customElements.define(
      'qp-header',
      class QpHeader extends HTMLElement {
        connectedCallback() {
          this._lastY = window.scrollY;
          this._ticking = false;
          var qpHeaderSelf = this;
          syncMinimogLayoutVars(this);
          this._onLayoutResize = function () {
            clearTimeout(qpHeaderSelf._layoutResizeTimer);
            qpHeaderSelf._layoutResizeTimer = setTimeout(function () {
              syncMinimogLayoutVars(qpHeaderSelf);
            }, 200);
          };
          window.addEventListener('resize', this._onLayoutResize);

          var wrap = this.closest('.shopify-section');
          if (wrap && this.dataset.sticky !== 'none') {
            wrap.classList.add('qp-header-sticky-wrap');
          }

          this._onScroll = this._onScroll.bind(this);
          if (this.dataset.sticky !== 'none') {
            window.addEventListener('scroll', this._onScroll, { passive: true });
          }
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this._onScroll);
          window.removeEventListener('resize', this._onLayoutResize);
        }

        /* sticky: condense / hide / reveal */
        _onScroll() {
          if (this._ticking) return;
          this._ticking = true;
          var self = this;
          requestAnimationFrame(function () {
            var y = window.scrollY;
            self.classList.toggle('qp-header--condensed', y > CONDENSE_AT);
            if (y <= CONDENSE_AT) {
              self._setHidden(false, true);
            } else if (y > self._lastY && y > HIDE_AFTER) {
              self._setHidden(true);
            } else if (y < self._lastY && !atcPinned()) {
              self._setHidden(false);
            }
            self._lastY = y;
            self._ticking = false;
          });
        }

        _setHidden(hidden, atRest) {
          this.classList.toggle('qp-header--hidden', hidden);
          document.body.classList.toggle('header-scroll-down', hidden);
          document.body.classList.toggle(
            'header-scroll-up',
            !hidden && !atRest
          );
        }
      }
    );
  }

  /* ── 2 · door drawers (qp-shell.css contract) ───────────────────────── */

  var drawerOpenRoot = null;
  var drawerLastTrigger = null;

  function drawerTrap(e) {
    if (e.key !== 'Tab') return;
    var root = drawerOpenRoot;
    if (!root) return;
    var focusables = root.querySelectorAll(
      'a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function drawerOpen(trigger) {
    var root = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!root) return;
    if (drawerOpenRoot === root) {
      drawerClose(true); /* re-click on the open door toggles it closed */
      return;
    }
    if (drawerOpenRoot) drawerClose();

    /* cancel a pending close -> hidden, or a quick close->reopen would
       vanish the freshly opened drawer when the stale timeout fired */
    clearTimeout(root._qpHideTimer);
    drawerOpenRoot = root;
    drawerLastTrigger = trigger;
    root.hidden = false;
    /* double rAF so the transform transition runs from the closed frame
       (shell-contract state 2), not a snap */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (drawerOpenRoot === root) root.classList.add('is-open');
      });
    });
    document.body.classList.add('qp-scroll-lock');
    trigger.setAttribute('aria-expanded', 'true');
    var closeBtn = root.querySelector('[data-qp-drawer-close]');
    if (closeBtn) closeBtn.focus();
    root.addEventListener('keydown', drawerTrap);
  }

  function drawerClose(refocus) {
    var root = drawerOpenRoot;
    if (!root) return;
    drawerOpenRoot = null;
    root.classList.remove('is-open');
    root.removeEventListener('keydown', drawerTrap);
    document.body.classList.remove('qp-scroll-lock');
    var delay = reducedMotion.matches ? 0 : 280;
    clearTimeout(root._qpHideTimer);
    root._qpHideTimer = setTimeout(function () {
      if (!root.classList.contains('is-open')) root.hidden = true;
    }, delay);
    var trigger = drawerLastTrigger;
    drawerLastTrigger = null;
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      if (refocus !== false) trigger.focus();
    }
  }

  document.addEventListener('click', function (e) {
    var door = e.target.closest('[data-qp-drawer-open]');
    if (door) {
      drawerOpen(door);
      return;
    }
    if (e.target.closest('[data-qp-drawer-close]')) {
      drawerClose(true);
      return;
    }
    /* outside click: anything not inside the open drawer's panel closes
       it — the shell scrim (inset:0) catches every stray click */
    if (drawerOpenRoot) {
      var panel = drawerOpenRoot.querySelector('.qp-shell__panel');
      if (!panel || !panel.contains(e.target)) drawerClose(false);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawerOpenRoot) drawerClose(true);
  });

  /* F6§14 hover clause RETIRED (PM live test 2026-07-12, same-day):
     the BIKES hover-peek (AJAR/ENGAGED state machine that briefly lived
     here) "flashes as soon as there is a small mouse movement" — the
     drawer+scrim is too heavy an object for hover. REV D's click-only
     ruling is restored as signed canon: hover NEVER opens a drawer;
     doors open on click/Enter/Space only. The F6§14 WAYFINDING pieces
     (YOU-ARE-HERE drawer mark, category ledger §3c) were the permanent-
     value half and remain. */

  /* ── 3a · mobile LEFT sheet ─────────────────────────────────────────── */

  var sheetLastTrigger = null;
  var sheetHideTimer = null;

  function sheetRoot() {
    return document.querySelector('[data-qp-sheet-root]');
  }

  function sheetOpen(trigger) {
    var root = sheetRoot();
    if (!root) return;
    /* cancel a pending close -> hidden, or a quick close->reopen would
       vanish the freshly opened sheet when the stale timeout fired */
    clearTimeout(sheetHideTimer);
    sheetLastTrigger = trigger || null;
    root.hidden = false;
    /* double rAF so the transform transition runs from the closed state */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        root.classList.add('is-open');
      });
    });
    document.body.style.overflow = 'hidden';
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    var closeBtn = root.querySelector('[data-qp-sheet-close]');
    if (closeBtn) closeBtn.focus();
    root.addEventListener('keydown', sheetTrap);
  }

  function sheetClose() {
    var root = sheetRoot();
    if (!root || root.hidden) return;
    root.classList.remove('is-open');
    root.removeEventListener('keydown', sheetTrap);
    document.body.style.overflow = '';
    var burger = document.querySelector('[data-qp-sheet-open]');
    if (burger) burger.setAttribute('aria-expanded', 'false');
    var delay = reducedMotion.matches ? 120 : 280;
    clearTimeout(sheetHideTimer);
    sheetHideTimer = setTimeout(function () {
      if (!root.classList.contains('is-open')) root.hidden = true;
    }, delay);
    if (sheetLastTrigger) sheetLastTrigger.focus();
  }

  function sheetTrap(e) {
    if (e.key !== 'Tab') return;
    var root = sheetRoot();
    if (!root) return;
    var focusables = root.querySelectorAll(
      'a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function sheetCollapse(toggle) {
    var sub = document.getElementById(toggle.getAttribute('aria-controls'));
    toggle.setAttribute('aria-expanded', 'false');
    if (sub) sub.hidden = true;
    var row = toggle.closest('.qp-sheet__row');
    if (row) row.classList.remove('is-expanded');
  }

  document.addEventListener('click', function (e) {
    var open = e.target.closest('[data-qp-sheet-open]');
    if (open) {
      sheetOpen(open);
      return;
    }
    var subToggle = e.target.closest('[data-qp-sheet-sub-toggle]');
    if (subToggle) {
      var expanded = subToggle.getAttribute('aria-expanded') === 'true';
      if (expanded) {
        sheetCollapse(subToggle);
        return;
      }
      /* EXCLUSIVE expansion: the expanded row is the view's one kinetic-
         lime highlight — collapse any other open row first */
      var root = sheetRoot();
      if (root) {
        root
          .querySelectorAll('[data-qp-sheet-sub-toggle][aria-expanded="true"]')
          .forEach(sheetCollapse);
      }
      var sub = document.getElementById(subToggle.getAttribute('aria-controls'));
      subToggle.setAttribute('aria-expanded', 'true');
      if (sub) sub.hidden = false;
      var row = subToggle.closest('.qp-sheet__row');
      if (row) row.classList.add('is-expanded');
      return;
    }
    if (
      e.target.closest('[data-qp-sheet-close]') ||
      e.target.closest('[data-qp-sheet-scrim]')
    ) {
      sheetClose();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var root = sheetRoot();
    if (root && !root.hidden) sheetClose();
  });

  /* support hand-off: same launcher mechanism as the cart page, but bound
     INSIDE the sheet/drawers with stopPropagation so the cart page's own
     document-level [data-qp-open-chat] delegate can never double-fire
     (launcher.click() twice would toggle the chat open then closed). */
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest('[data-qp-open-chat]');
      if (!btn) return;
      var inSheet = btn.closest('[data-qp-nav-sheet]');
      var inDrawer = btn.closest('[data-qp-drawer]');
      if (!inSheet && !inDrawer) return;
      e.stopPropagation();
      if (inSheet) sheetClose();
      if (inDrawer) drawerClose(false);
      var launcher =
        document.querySelector('#qpu-launcher') ||
        document.querySelector('#tidio-chat iframe, #tidio-chat-iframe');
      if (launcher && typeof launcher.click === 'function') launcher.click();
    },
    true
  );

  /* ── 3b · market selector (ruling 09) ───────────────────────────────── */

  if (!customElements.get('qp-market-selector')) {
    customElements.define(
      'qp-market-selector',
      class QpMarketSelector extends HTMLElement {
        connectedCallback() {
          this._trigger = this.querySelector('[data-qp-market-trigger]');
          this._list = this.querySelector('[role="listbox"]');
          this._input = this.querySelector('[data-qp-market-input]');
          if (!this._trigger || !this._list || !this._input) return;

          var self = this;
          this._trigger.addEventListener('click', function () {
            self._toggle();
          });
          this._list.addEventListener('click', function (e) {
            var opt = e.target.closest('[data-qp-market-option]');
            if (opt) self._select(opt);
          });
          this.addEventListener('keydown', function (e) {
            self._onKeydown(e);
          });
          this._onDocClick = function (e) {
            if (!self.contains(e.target)) self._close();
          };
          document.addEventListener('click', this._onDocClick);
        }

        disconnectedCallback() {
          document.removeEventListener('click', this._onDocClick);
        }

        _isOpen() {
          return this._trigger.getAttribute('aria-expanded') === 'true';
        }

        _toggle() {
          if (this._isOpen()) this._close();
          else this._open();
        }

        _open() {
          this._trigger.setAttribute('aria-expanded', 'true');
          this._list.hidden = false;
          var current =
            this._list.querySelector('[aria-selected="true"]') ||
            this._list.querySelector('[data-qp-market-option]');
          if (current) current.focus();
        }

        _close(refocus) {
          if (!this._isOpen()) return;
          this._trigger.setAttribute('aria-expanded', 'false');
          this._list.hidden = true;
          if (refocus) this._trigger.focus();
        }

        _options() {
          return Array.prototype.slice.call(
            this._list.querySelectorAll('[data-qp-market-option]')
          );
        }

        _onKeydown(e) {
          if (e.key === 'Escape') {
            this._close(true);
            return;
          }
          if (!this._isOpen()) {
            if (
              (e.key === 'ArrowDown' || e.key === 'ArrowUp') &&
              e.target === this._trigger
            ) {
              e.preventDefault();
              this._open();
            }
            return;
          }
          var opts = this._options();
          var i = opts.indexOf(document.activeElement);
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            var next = e.key === 'ArrowDown' ? i + 1 : i - 1;
            if (next < 0) next = opts.length - 1;
            if (next >= opts.length) next = 0;
            opts[next].focus();
          } else if (e.key === 'Home') {
            e.preventDefault();
            opts[0].focus();
          } else if (e.key === 'End') {
            e.preventDefault();
            opts[opts.length - 1].focus();
          } else if (e.key === 'Tab') {
            this._close();
          }
        }

        /* country + currency switch TOGETHER via the native localization
           form — Markets re-renders every price server-side. */
        _select(opt) {
          this._input.value = opt.getAttribute('data-value');
          var form = this._input.closest('form');
          if (form) form.submit();
        }
      }
    );
  }

  /* ── 3c · category ledger "Change category" -> BIKES door/sheet (F6§14) ─
     snippets/qp-category-ledger.liquid's button doesn't know (and
     shouldn't need to know) whether the desktop bar or the mobile burger
     is showing — it just carries [data-qp-ledger-open-bikes]. This finds
     the REAL BIKES door / sheet sub-toggle at click time and reuses their
     existing open mechanics exactly (drawerOpen / the sheet's own
     exclusive-expansion click delegate), so there is exactly one source
     of truth for "how BIKES opens" on each breakpoint. */

  /* must match the bar's own breakpoint (qp-nav.css .qp-header__nav) so
     the ledger opens the surface that is actually rendered — bumped
     1024 -> 1360 with the tablet-collision fix (iPad Pro 12.9 portrait). */
  var desktopNav = window.matchMedia
    ? window.matchMedia('(min-width: 1360px)')
    : { matches: false };

  document.addEventListener('click', function (e) {
    var ledgerBtn = e.target.closest('[data-qp-ledger-open-bikes]');
    if (!ledgerBtn) return;

    if (desktopNav.matches) {
      var door = document.querySelector(
        '[data-qp-drawer-open][data-qp-door="door_bikes"]'
      );
      if (door) drawerOpen(door);
      return;
    }

    var root = sheetRoot();
    if (root && root.hidden) sheetOpen(ledgerBtn);
    var toggle = document.querySelector(
      '[data-qp-sheet-sub-toggle][data-qp-door="door_bikes"]'
    );
    if (toggle && toggle.getAttribute('aria-expanded') !== 'true') {
      toggle.click(); /* the sheet's own exclusive-expansion delegate owns this */
    }
  });

  /* ── 4 · cart bubble = BUILDS + ITEMS (ruling 04) ───────────────────── */

  var bubbleWriting = false;
  var bubbleLast = null;
  var bubbleFetchSeq = 0;

  function qpBubbles() {
    return document.querySelectorAll('.m-cart-count-bubble');
  }

  function qpSetBubbles(val) {
    bubbleWriting = true;
    bubbleLast = String(val);
    qpBubbles().forEach(function (el) {
      if (!val || val === '0') {
        el.classList.add('m:hidden');
        el.textContent = val || '0';
      } else {
        el.textContent = val;
        el.classList.remove('m:hidden');
        if (!reducedMotion.matches) {
          el.classList.remove('qp-bubble-tick');
          void el.offsetWidth;
          el.classList.add('qp-bubble-tick');
        }
      }
    });
    bubbleWriting = false;
  }

  function qpRefreshBubble(itemCount) {
    if (itemCount === 0) {
      qpSetBubbles('0');
      return;
    }
    var seq = ++bubbleFetchSeq;
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/';
    fetch(root + '?sections=qp-nav-count')
      .then(function (r) {
        return r.json();
      })
      .then(function (payload) {
        if (seq !== bubbleFetchSeq) return; /* a newer update superseded us */
        var html = payload && payload['qp-nav-count'];
        if (!html) return;
        var m = html.match(/data-qp-count="([^"]*)"/);
        if (m) qpSetBubbles(m[1]);
      })
      .catch(function () {
        /* keep the current (server-rendered) value — never fall back to
           raw item_count, the count vocabulary is builds + items */
      });
  }

  function qpCountArg(c) {
    if (typeof c === 'number') return c;
    if (c && typeof c.item_count === 'number') return c.item_count;
    return null;
  }

  /* patch every Minimog element that owns updateCartCount — same
     customElements pattern as qp-cart-build.js. whenDefined keeps this
     order-independent (qp-nav.js loads before cart-drawer.js). */
  ['m-cart-drawer', 'm-cart-drawer-items', 'm-cart'].forEach(function (name) {
    customElements.whenDefined(name).then(function () {
      var Cls = customElements.get(name);
      if (!Cls) return;
      Cls.prototype.updateCartCount = function (c) {
        qpRefreshBubble(qpCountArg(c));
      };
    });
  });

  /* backstop: anything that writes item_count straight into the bubble
     (theme-global.js no-drawer path, third-party apps) gets corrected. */
  function qpObserveBubbles() {
    var debounce = null;
    qpBubbles().forEach(function (el) {
      new MutationObserver(function () {
        if (bubbleWriting) return;
        if (el.textContent === bubbleLast) return;
        clearTimeout(debounce);
        debounce = setTimeout(function () {
          qpRefreshBubble(null);
        }, 80);
      }).observe(el, { childList: true, characterData: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', qpObserveBubbles);
  } else {
    qpObserveBubbles();
  }
})();
